import React, {useState, useEffect, useMemo} from "react";

import Stockchart from "../../../components/Stockchart";

import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";

import {VsiBubble, VsiH4} from "../../../styled/App.styled";
import {Card} from "reactstrap";
import Grid from "../../../components/Grid";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {useSearchParams} from "react-router-dom";
import {formatDualAxisStockChartConfig, formatStockChartConfig, toSnakeCase} from "../../../utils/Helpers";
import {
    allCapacityTrendDefs,
    allCapacityByDataCenterTrendDefs,
    allPerformanceTrendDefs,
    allCapacityByDeviceTypeTrendDefs,
    allCapacityByClassificationTrendDefs,
    deviceDataReductionRatioDefs,
    allCapacityByApplicationTrendDefs,
    allPhysCapacityTrendDefs,
    colDateDef,
    deviceTypeDRRDefs, allCapacityByCompanyTrendDefs, allCapacityByGeographyTrendDefs
} from "../../../utils/gridColumnDefs/storage/enterpriseTrends";
import {allPerformanceTypes, allPhysSpaceTypes, allSpaceTypes} from "../../../utils/utils";

import {
    standardCapacityColors,
    allPerformance, genericColors, enterpriseTrendColors
} from "../../../utils/chartUtils";

import {chartTypes, chartBUTypes} from "../../../utils/utils";
import {useVsiAppContext} from "../../../stores/VsiApp";

const possibleAdditionals = ['Data Center', 'Device Type', 'Classification', 'Space Type', 'Performance Type'];

const chartMetrics = [
    {value: 'history', label: 'History', route: '_proc', yAxis: 'GiB'},
    {value: 'by_datacenter', label: 'By Data Center', route: '_by_datacenter_proc', yAxis: 'Ratio'},
    {value: 'by_device_type', label: 'By Device Type', route: '_by_device_type_proc', yAxis: '# Red Light Alerts'},
    {value: 'by_classification', label: 'By Classification', route: '_by_classification_proc', yAxis: 'GiB'},
    {value: 'by_application', label: 'By Application', route: '_by_application_proc', yAxis: 'GiB'},
    {value: 'by_company', label: 'By Company', route: '_by_company_proc', yAxis: 'GiB'},
    {value: 'by_geography', label: 'By Geography', route: '_by_geography_proc', yAxis: 'GiB'},
    {value: 'all_datacenters', label: 'All Data Centers', route: '_by_all_datacenters_proc', yAxis: 'GiB'},
    {value: 'all_device_types', label: 'All Device Types', route: '_by_all_device_types_proc', yAxis: 'GiB'},
    {value: 'all_classifications', label: 'All Classifications', route: '_by_all_classification_proc', yAxis: 'GiB'},
    {value: 'all_applications', label: 'All Applications', route: '_by_all_application_proc', yAxis: 'GiB'},
    {value: 'all_company', label: 'All Companies', route: '_by_all_company_proc', yAxis: 'GiB'},
    {value: 'all_geography', label: 'All Geographies', route: '_by_all_geography_proc', yAxis: 'GiB'},
];

const ratioChartMetrics = [
    {value: 'all_datacenters', label: 'All Data Centers', yAxis: 'GiB'},
    {value: 'all_device_types', label: 'All Device Types', yAxis: 'GiB'},
    {value: 'all_classifications', label: 'All Classifications', yAxis: 'GiB'},
    {value: 'all_applications', label: 'All Applications', yAxis: 'GiB'},
    {value: 'all_company', label: 'All Companies', yAxis: 'GiB'},
    {value: 'all_geography', label: 'All Geographies', yAxis: 'GiB'},
    {value: 'by_datacenter', label: 'By Data Center', yAxis: 'GiB'},
    {value: 'by_device_type', label: 'By Device Type', yAxis: 'GiB'},
    {value: 'by_classification', label: 'By Classification', yAxis: 'GiB'},
    {value: 'by_application', label: 'By Application', yAxis: 'GiB'},
    {value: 'by_company', label: 'By Company', yAxis: 'GiB'},
    {value: 'by_geography', label: 'By Geography', yAxis: 'GiB'},
];

export default function EnterpriseTrends({title, context}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();

    const [searchParams, setSearchParams] = useSearchParams();

    const type = searchParams.get('type') || 'capacity';
    let defaultType = chartTypes.find(({value}) => type === value) || chartTypes[0];
    const [chartType, setChartType] = useState(defaultType);

    let chosenMetrics = chartType.value === 'data_reduction' ? ratioChartMetrics : chartMetrics;

    const metric = searchParams.get('metric') || 'history';
    let defaultMetric = chosenMetrics.find(({value}) => metric === value) || chosenMetrics[0];

    const [chartMetric, setChartMetric] = useState(defaultMetric);
    const [activeMetricSelection, setActiveMetricSelection] = useState(chosenMetrics);

    const [chartData, setChartData] = useState([]);
    const [chartPerfData, setChartPerfData] = useState([]);
    const [chartDefs, setChartDefs] = useState([]);
    const [formattedChartData, setFormattedChartData] = useState([]);
    const [chartConfig, setChartConfig] = useState({
        colors: genericColors
    });

    const [additionalChartOption, setAdditionalChartOption] = useState({});
    const [additionalChartOptionSelection, setAdditionalChartOptionSelection] = useState([]);
    const [additionalChartOptionLabel, setAdditionalChartOptionLabel] = useState('');

    const getRouteForSelection = route => {
        if (searchParams.get('type') && searchParams.get('type') === 'data_reduction') {
            if (!searchParams.get('metric') || searchParams.get('metric') === 'all_device_types') {
                route = '_by_all_device_types';
            } else if (searchParams.get('metric') === 'all_classifications') {
                route = '_by_all_classification';
            } else if (searchParams.get('metric') === 'all_applications') {
                route = '_by_all_application';
            } else if (searchParams.get('metric') === 'all_datacenters') {
                route = '_by_all_datacenters';
            } else if (searchParams.get('metric') === 'all_company') {
                route = '_by_all_company';
            } else if (searchParams.get('metric') === 'all_geography') {
                route = '_by_all_geography';
            } else {
                route = `_${searchParams.get('metric')}`;
            }
            route += '_proc';
        }

        let routeToSearch = `/api/v6/report/enterprise_trends${route}?client_id=${client.clientId}&period_id=${periodId}`;

        if (chartMetric.value === 'by_datacenter' && searchParams.get('data_center')) {
            routeToSearch += `&data_center=${searchParams.get('data_center')}`;
        } else if (chartMetric.value === 'by_device_type' && searchParams.get('device_type')) {
            routeToSearch += `&device_type=${searchParams.get('device_type')}`;
        } else if (chartMetric.value === 'by_classification' && searchParams.get('classification')) {
            routeToSearch += `&classification=${searchParams.get('classification')}`;
        } else if (chartMetric.value === 'by_application' && searchParams.get('application')) {
            routeToSearch += `&application=${searchParams.get('application')}`;
        } else if (chartMetric.value === 'by_company' && searchParams.get('company')) {
            routeToSearch += `&company_name=${searchParams.get('company')}`;
        } else if (chartMetric.value === 'by_geography' && searchParams.get('geography')) {
            routeToSearch += `&geography=${searchParams.get('geography')}`;
        }

        return routeToSearch;
    };

    const { route: activeRoute } = chartMetric;

    const trendRoute = useMemo(() => getRouteForSelection(activeRoute), [activeRoute, searchParams]);

    const [data, {isLoading}] = useDataWithRoute(trendRoute);

    const addtlParam = useMemo(() => searchParams.get(toSnakeCase(additionalChartOptionLabel)), [additionalChartOptionLabel, searchParams]);

    useEffect(() => {
        setChartData(data?.recordsets[0]);
        setChartPerfData(data?.recordsets[1]);
        handleStateWithData(data?.recordsets || [[], []]);
        setChartDefs(defineColumnDefs(chartMetric.value))
    }, [data, addtlParam, chartType, activeMetricSelection]);


    const onTypeChange = (type) => {
        setChartType(type);
        setActiveMetricSelection(type.value === 'data_reduction' ? ratioChartMetrics : chartMetrics);
        setChartMetric(type.value === 'data_reduction' ? ratioChartMetrics[0] : chartMetrics[0]);
        searchParams.set('type', type.value);
        searchParams.set('metric', type.value === 'data_reduction' ? ratioChartMetrics[0].value : chartMetrics[0].value)
        setSearchParams(searchParams, {replace: true});
    };

    const onMetricChange = async (metric) => {
        setChartMetric(metric);
        searchParams.set('metric', metric.value);
        searchParams.delete('classification');
        searchParams.delete('application');
        searchParams.delete('device_type');
        setSearchParams(searchParams, {replace: true});
        getRouteForSelection(metric.route);
    };

    const onAdditionalOptionChange = async (option) => {
        setAdditionalChartOption(option);
        searchParams.set(toSnakeCase(additionalChartOptionLabel), option.value);
        setSearchParams(searchParams, {replace: true});

    };

    const clearAdditionalMetricSearch = () => {
        possibleAdditionals.forEach(additional => {
            searchParams.delete(toSnakeCase(additional));
            setSearchParams(searchParams);
        });
    };

    const setAllAdditionalParams = (selection, metric) => {
        let label = additionalChartOptionLabel;
        let select = [...additionalChartOptionSelection];
        let addtl = {...additionalChartOption};
        if (additionalChartOptionLabel !== metric) {
            label = metric;
        }
        if (additionalChartOptionLabel === '' || additionalChartOptionLabel !== metric || selection.length > 0) {
            select = [...selection];
            addtl = {...select[0]};
        }
        possibleAdditionals.forEach(additional => {
            if (additional !== metric) {
                searchParams.delete(toSnakeCase(additional));
                setSearchParams(searchParams);
            }
        });
        let search = searchParams.get(toSnakeCase(metric));
        if (search !== addtl.value) {
            addtl = {...select.find(({value}) => value === search) || addtl}
        }

        setAdditionalChartOptionSelection(select);
        setAdditionalChartOptionLabel(label);
        setAdditionalChartOption(addtl);
    };

    const defineColumnDefs = (metric) => {
        let defsArray = [...chartDefs];

        switch (metric) {
            case 'history':
                if (chartType.value === "capacity") {
                    defsArray = colDateDef.concat(allCapacityTrendDefs);
                } else if (chartType.value === "phys_capacity") {
                    defsArray = colDateDef.concat(allPhysCapacityTrendDefs);
                } else {
                    defsArray = chartType.value === 'performance' ? colDateDef.concat(allPerformanceTrendDefs) : colDateDef.concat(deviceDataReductionRatioDefs);
                }
                break;
            case 'all_datacenters':
            case 'by_datacenter':
                if (chartType.value === "capacity") {
                    defsArray = colDateDef.concat(allCapacityByDataCenterTrendDefs).concat(allCapacityTrendDefs);
                } else if (chartType.value === "phys_capacity") {
                    defsArray = colDateDef.concat(allCapacityByDataCenterTrendDefs).concat(allPhysCapacityTrendDefs);
                } else if (chartType.value === "data_reduction") {
                    defsArray = colDateDef.concat(deviceDataReductionRatioDefs);
                } else {
                    defsArray = colDateDef.concat(allCapacityByDataCenterTrendDefs).concat(allPerformanceTrendDefs);
                }
                break;
            case 'all_device_types':
            case 'by_device_type':
                if (chartType.value === "capacity") {
                    defsArray = colDateDef.concat(allCapacityByDeviceTypeTrendDefs).concat(allCapacityTrendDefs);
                } else if (chartType.value === "phys_capacity") {
                    defsArray = colDateDef.concat(allCapacityByDeviceTypeTrendDefs).concat(allPhysCapacityTrendDefs);
                } else if (chartType.value === "data_reduction") {
                    defsArray = colDateDef.concat(deviceTypeDRRDefs).concat(deviceDataReductionRatioDefs);
                } else {
                    defsArray = colDateDef.concat(allCapacityByDeviceTypeTrendDefs).concat(allPerformanceTrendDefs);
                }
                break;
            case 'all_classifications':
            case 'by_classification':
                if (chartType.value === "capacity") {
                    defsArray = colDateDef.concat(allCapacityByClassificationTrendDefs).concat(allCapacityTrendDefs);
                } else if (chartType.value === "phys_capacity") {
                    defsArray = colDateDef.concat(allCapacityByClassificationTrendDefs).concat(allPhysCapacityTrendDefs);
                } else if (chartType.value === "data_reduction") {
                    defsArray = colDateDef.concat(allCapacityByClassificationTrendDefs).concat(deviceDataReductionRatioDefs);
                } else {
                    defsArray = colDateDef.concat(allCapacityByClassificationTrendDefs).concat(allPerformanceTrendDefs);
                }
                break;
            case 'all_applications':
            case 'by_application':
                if (chartType.value === "capacity") {
                    defsArray = colDateDef.concat(allCapacityByApplicationTrendDefs).concat(allCapacityTrendDefs);
                } else if (chartType.value === "phys_capacity") {
                    defsArray = colDateDef.concat(allCapacityByApplicationTrendDefs).concat(allPhysCapacityTrendDefs);
                } else if (chartType.value === "data_reduction") {
                    defsArray = colDateDef.concat(allCapacityByApplicationTrendDefs).concat(deviceDataReductionRatioDefs);
                } else {
                    defsArray = colDateDef.concat(allCapacityByApplicationTrendDefs).concat(allPerformanceTrendDefs);
                }
                break;
            case 'all_company':
            case 'by_company':
                if (chartType.value === "capacity") {
                    defsArray = colDateDef.concat(allCapacityByCompanyTrendDefs).concat(allCapacityTrendDefs);
                } else if (chartType.value === "phys_capacity") {
                    defsArray = colDateDef.concat(allCapacityByCompanyTrendDefs).concat(allPhysCapacityTrendDefs);
                } else if (chartType.value === "data_reduction") {
                    defsArray = colDateDef.concat(allCapacityByCompanyTrendDefs).concat(deviceDataReductionRatioDefs);
                } else {
                    defsArray = colDateDef.concat(allCapacityByCompanyTrendDefs).concat(allPerformanceTrendDefs);
                }
                break;
            case 'all_geography':
            case 'by_geography':
                if (chartType.value === "capacity") {
                    defsArray = colDateDef.concat(allCapacityByGeographyTrendDefs).concat(allCapacityTrendDefs);
                } else if (chartType.value === "phys_capacity") {
                    defsArray = colDateDef.concat(allCapacityByGeographyTrendDefs).concat(allPhysCapacityTrendDefs);
                } else if (chartType.value === "data_reduction") {
                    defsArray = colDateDef.concat(allCapacityByGeographyTrendDefs).concat(deviceDataReductionRatioDefs);
                } else {
                    defsArray = colDateDef.concat(allCapacityByGeographyTrendDefs).concat(allPerformanceTrendDefs);
                }
                break;
            default:
                break;
        }

        return defsArray;
    };

    const handleStateWithData = data => {
        let config = null;

        let sortedData = data?.[0]?.sort((a, b) => new Date(a.collection_date) - new Date(b.collection_date));
        switch (chartMetric.value) {
            case "history":
                if (chartType.value === "capacity") {
                    config = formatStockChartConfig(sortedData || [], [
                        {
                            name: 'Free',
                            field: 'free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Used',
                            field: 'used',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Allocated',
                            field: 'allocated',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Capacity',
                            field: 'capacity',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Orphaned',
                            field: 'orphaned',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Snap',
                            field: 'snap',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        }
                    ]);
                    setChartConfig({
                        ...chartConfig,
                        colors: enterpriseTrendColors,
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "phys_capacity") {
                    config = formatStockChartConfig(sortedData || [], [
                        {
                            name: 'Phys. Free',
                            field: 'physical_free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Phys. Used',
                            field: 'physical_used',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Allocated',
                            field: 'allocated',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Phys. Capacity',
                            field: 'physical_capacity',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Orphaned',
                            field: 'orphaned',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Snap',
                            field: 'snap',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        }
                    ]);
                    setChartConfig({
                        ...chartConfig,
                        colors: enterpriseTrendColors,
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else {
                    let sortedData = data?.[1]?.sort((a, b) => new Date(a.collection_date) - new Date(b.collection_date));
                    config = formatDualAxisStockChartConfig(sortedData || [],
                        [
                            {
                                name: 'IOPS',
                                field: 'iops',
                                yAxis: 0,
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Latency',
                                field: 'latency',
                                yAxis: 1
                            },
                        ]);
                    setChartConfig({
                        colors: allPerformance,
                        yAxis: [
                            {
                                title: {
                                    text: 'IOPS',
                                },
                                opposite: false
                            },
                            {
                                title: {
                                    text: 'Latency (ms)',
                                },
                                opposite: true
                            }
                        ]
                    });

                }

                setFormattedChartData(config.series);
                setAdditionalChartOptionSelection([]);
                setAdditionalChartOption({});
                clearAdditionalMetricSearch();
                break;
            case "by_datacenter":
                if (chartType.value === "capacity") {
                    config = formatStockChartConfig(sortedData || [], [
                        {
                            name: 'Free',
                            field: 'free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Used',
                            field: 'used',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Allocated',
                            field: 'allocated',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Capacity',
                            field: 'capacity',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Orphaned',
                            field: 'orphaned',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Snap',
                            field: 'snap',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        }
                    ])
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.data_center,
                            label: datum.data_center,
                            route: `data_center=${datum.data_center}`
                        };
                    }), 'Data Center');
                    setChartConfig({
                        colors: enterpriseTrendColors,
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "phys_capacity") {
                    config = formatStockChartConfig(sortedData || [], [
                        {
                            name: 'Phys. Free',
                            field: 'physical_free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Phys. Used',
                            field: 'physical_used',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Allocated',
                            field: 'allocated',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Phys. Capacity',
                            field: 'physical_capacity',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Orphaned',
                            field: 'orphaned',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Snap',
                            field: 'snap',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        }
                    ]);
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.data_center,
                            label: datum.data_center,
                            route: `data_center=${datum.data_center}`
                        };
                    }), 'Data Center');
                    setChartConfig({
                        ...chartConfig,
                        colors: enterpriseTrendColors,
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "data_reduction") {
                    if (!searchParams.get("data_center") && data[1]?.[0].data_center) {
                        searchParams.set("data_center", data[1][0].data_center)
                        setSearchParams(searchParams, {replace: true});
                    }
                    config = formatStockChartConfig(sortedData || [],
                        [{name: 'Data Reduction Ratio', field: 'DRR',}]
                    );

                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.data_center,
                            label: datum.data_center,
                            route: `data_center=${datum.data_center}`
                        };
                    }), 'Data Center');

                    setChartConfig({
                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'Data Reduction Ratio'
                            },
                        }
                    });
                } else {
                    config = formatDualAxisStockChartConfig(sortedData || [],
                        [
                            {
                                name: 'IOPS',
                                field: 'iops',
                                yAxis: 0,
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Latency',
                                field: 'latency',
                                yAxis: 1
                            },
                        ]);

                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.data_center,
                            label: datum.data_center,
                            route: `data_center=${datum.data_center}`
                        };
                    }), 'Data Center');
                    setChartConfig({
                        colors: allPerformance,
                        yAxis: [
                            {
                                title: {
                                    text: 'IOPS',
                                },
                                opposite: false
                            },
                            {
                                title: {
                                    text: 'Latency (ms)',
                                },
                                opposite: true
                            }
                        ]
                    });
                }
                setFormattedChartData(config.series);
                break;
            case "by_device_type":
                if (chartType.value === "capacity") {
                    config = formatStockChartConfig(sortedData || [], [
                        {
                            name: 'Free',
                            field: 'free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Used',
                            field: 'used',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Allocated',
                            field: 'allocated',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Capacity',
                            field: 'capacity',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Orphaned',
                            field: 'orphaned',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Snap',
                            field: 'snap',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        }
                    ])
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.device_type_code,
                            label: datum.device_type_description,
                            route: `device_type=${datum.device_type_code}`
                        };
                    }), 'Device Type');
                    setChartConfig({
                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "phys_capacity") {
                    config = formatStockChartConfig(sortedData || [], [
                        {
                            name: 'Phys. Free',
                            field: 'physical_free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Phys. Used',
                            field: 'physical_used',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Allocated',
                            field: 'allocated',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Phys. Capacity',
                            field: 'physical_capacity',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Orphaned',
                            field: 'orphaned',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Snap',
                            field: 'snap',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        }
                    ]);
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.device_type_code,
                            label: datum.device_type_description,
                            route: `device_type=${datum.device_type_code}`
                        };
                    }), 'Device Type');
                    setChartConfig({
                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "data_reduction") {
                    if (!searchParams.get("device_type") && data[1]?.[0].device_type_code) {
                        searchParams.set("device_type", data[1][0].device_type_code)
                        setSearchParams(searchParams, {replace: true});
                    }
                    config = formatStockChartConfig(sortedData || [],
                        [{name: 'Data Reduction Ratio', field: 'DRR',}]
                    );

                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.device_type_code,
                            label: datum.device_type_description,
                            route: `device_type=${datum.device_type_code}`
                        };
                    }), 'Device Type');

                    setChartConfig({
                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'Data Reduction Ratio'
                            },
                        }
                    });
                } else {
                    config = formatDualAxisStockChartConfig(sortedData || [],
                        [
                            {
                                name: 'IOPS',
                                field: 'iops',
                                yAxis: 0,
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Latency',
                                field: 'latency',
                                yAxis: 1
                            },
                        ]);
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.device_type_code,
                            label: datum.device_type_description,
                            route: `device_type=${datum.device_type_code}`
                        };
                    }), 'Device Type');
                    setChartConfig({
                        colors: allPerformance,
                        yAxis: [
                            {
                                title: {
                                    text: 'IOPS',
                                },
                                opposite: false
                            },
                            {
                                title: {
                                    text: 'Latency (ms)',
                                },
                                opposite: true
                            }
                        ]
                    });
                }
                setFormattedChartData(config.series);
                break;
            case "by_classification":
                if (chartType.value === "capacity") {
                    config = formatStockChartConfig(sortedData || [], [
                        {
                            name: 'Free',
                            field: 'free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Used',
                            field: 'used',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Allocated',
                            field: 'allocated',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Capacity',
                            field: 'capacity',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Orphaned',
                            field: 'orphaned',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Snap',
                            field: 'snap',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        }
                    ])
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.classification,
                            label: datum.classification,
                            route: `classification=${datum.classification}`
                        };
                    }), 'Classification');
                    setChartConfig({

                        colors: [...standardCapacityColors],
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "phys_capacity") {
                    config = formatStockChartConfig(sortedData || [], [
                        {
                            name: 'Phys. Free',
                            field: 'physical_free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Phys. Used',
                            field: 'physical_used',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Allocated',
                            field: 'allocated',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Phys. Capacity',
                            field: 'physical_capacity',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Orphaned',
                            field: 'orphaned',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Snap',
                            field: 'snap',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        }
                    ]);
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.classification,
                            label: datum.classification,
                            route: `classification=${datum.classification}`
                        };
                    }), 'Classification');
                    setChartConfig({
                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "data_reduction") {
                    if (!searchParams.get("classification") && data[1]?.[0].classification) {
                        searchParams.set("classification", data[1][0].classification)
                        setSearchParams(searchParams, {replace: true});
                    }
                    ;
                    config = formatStockChartConfig(sortedData || [],
                        [{name: 'Data Reduction Ratio', field: 'DRR',}]
                    );

                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.classification,
                            label: datum.classification,
                            route: `classification=${datum.classification}`
                        };
                    }), 'Classification');

                    setChartConfig({
                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'Data Reduction Ratio'
                            },
                        }
                    });
                } else {
                    config = formatDualAxisStockChartConfig(sortedData || [],
                        [
                            {
                                name: 'IOPS',
                                field: 'iops',
                                yAxis: 0,
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Latency',
                                field: 'latency',
                                yAxis: 1
                            },
                        ]);
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.classification,
                            label: datum.classification,
                            route: `classification=${datum.classification}`
                        };
                    }), 'Classification');
                    setChartConfig({
                        colors: allPerformance,
                        yAxis: [
                            {
                                title: {
                                    text: 'IOPS',
                                },
                                opposite: false
                            },
                            {
                                title: {
                                    text: 'Latency (ms)',
                                },
                                opposite: true
                            }
                        ]
                    });
                }
                setFormattedChartData(config.series);
                break;
            case "by_application":
                if (chartType.value === "capacity") {
                    config = formatStockChartConfig(sortedData || [], [
                        {
                            name: 'Free',
                            field: 'free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Used',
                            field: 'used',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Allocated',
                            field: 'allocated',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Capacity',
                            field: 'capacity',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Orphaned',
                            field: 'orphaned',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Snap',
                            field: 'snap',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        }
                    ])
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.application,
                            label: datum.application,
                            route: `application=${datum.application}`
                        };
                    }), 'Application');
                    setChartConfig({

                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "phys_capacity") {
                    config = formatStockChartConfig(sortedData || [],
                        [{
                            name: 'Phys. Free',
                            field: 'physical_free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                            {
                                name: 'Phys. Used',
                                field: 'physical_used',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Allocated',
                                field: 'allocated',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Phys. Capacity',
                                field: 'physical_capacity',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Orphaned',
                                field: 'orphaned',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Snap',
                                field: 'snap',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            }
                        ]);
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.application,
                            label: datum.application,
                            route: `application=${datum.application}`
                        };
                    }), 'Application');
                    setChartConfig({
                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "data_reduction") {
                    if (!searchParams.get("application") && data[1]?.[0].application) {
                        searchParams.set("application", data[1][0].application)
                        setSearchParams(searchParams, {replace: true});
                    }
                    config = formatStockChartConfig(sortedData || [],
                        [{name: 'Data Reduction Ratio', field: 'DRR',}]
                    );

                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.application,
                            label: datum.application,
                            route: `application=${datum.application}`
                        };
                    }), 'Application');

                    setChartConfig({
                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'Data Reduction Ratio'
                            },
                        }
                    });
                } else { // Performance
                    config = formatDualAxisStockChartConfig(sortedData || [],
                        [
                            {
                                name: 'IOPS',
                                field: 'iops',
                                yAxis: 0,
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Latency',
                                field: 'latency',
                                yAxis: 1
                            },
                        ]);
                    let newData = data?.[1]?.filter(datum => datum.application);
                    setAllAdditionalParams((newData || []).map(datum => {
                        return {
                            value: datum.application,
                            label: datum.application,
                            route: `application=${datum.application}`
                        };
                    }), 'Application');
                    setChartConfig({
                        colors: allPerformance,
                        yAxis: [
                            {
                                title: {
                                    text: 'IOPS',
                                },
                                opposite: false
                            },
                            {
                                title: {
                                    text: 'Latency (ms)',
                                },
                                opposite: true
                            }
                        ]
                    });
                }
                setFormattedChartData(config.series);
                break;
            case "by_company":
                if (chartType.value === "capacity") {
                    config = formatStockChartConfig(sortedData || [], [
                        {
                            name: 'Free',
                            field: 'free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Used',
                            field: 'used',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Allocated',
                            field: 'allocated',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Capacity',
                            field: 'capacity',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Orphaned',
                            field: 'orphaned',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Snap',
                            field: 'snap',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        }
                    ])
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.company_name,
                            label: datum.company_name,
                            route: `company_name=${datum.company_name}`
                        };
                    }), 'Company');
                    setChartConfig({

                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "phys_capacity") {
                    config = formatStockChartConfig(sortedData || [],
                        [{
                            name: 'Phys. Free',
                            field: 'physical_free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                            {
                                name: 'Phys. Used',
                                field: 'physical_used',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Allocated',
                                field: 'allocated',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Phys. Capacity',
                                field: 'physical_capacity',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Orphaned',
                                field: 'orphaned',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Snap',
                                field: 'snap',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            }
                        ]);
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.company_name,
                            label: datum.company_name,
                            route: `company_name=${datum.company_name}`
                        };
                    }), 'Company');
                    setChartConfig({
                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "data_reduction") {
                    if (!searchParams.get("company") && data[1]?.[0].company_name) {
                        searchParams.set("company", data[1][0].company_name)
                        setSearchParams(searchParams, {replace: true});
                    }
                    config = formatStockChartConfig(sortedData || [],
                        [{name: 'Data Reduction Ratio', field: 'DRR',}]
                    );

                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.company_name,
                            label: datum.company_name,
                            route: `company_name=${datum.company_name}`
                        };
                    }), 'Company');

                    setChartConfig({
                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'Data Reduction Ratio'
                            },
                        }
                    });
                } else { // Performance
                    config = formatDualAxisStockChartConfig(sortedData || [],
                        [
                            {
                                name: 'IOPS',
                                field: 'iops',
                                yAxis: 0,
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Latency',
                                field: 'latency',
                                yAxis: 1
                            },
                        ]);
                    let newData = data?.[1]?.filter(datum => datum.company_name);
                    setAllAdditionalParams((newData || []).map(datum => {
                        return {
                            value: datum.company_name,
                            label: datum.company_name,
                            route: `company_name=${datum.company_name}`
                        };
                    }), 'Company');
                    setChartConfig({
                        colors: allPerformance,
                        yAxis: [
                            {
                                title: {
                                    text: 'IOPS',
                                },
                                opposite: false
                            },
                            {
                                title: {
                                    text: 'Latency (ms)',
                                },
                                opposite: true
                            }
                        ]
                    });
                }
                setFormattedChartData(config.series);
                break;
            case "by_geography":
                if (chartType.value === "capacity") {
                    config = formatStockChartConfig(sortedData || [], [
                        {
                            name: 'Free',
                            field: 'free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Used',
                            field: 'used',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Allocated',
                            field: 'allocated',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Capacity',
                            field: 'capacity',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Orphaned',
                            field: 'orphaned',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                        {
                            name: 'Snap',
                            field: 'snap',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        }
                    ])
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.geography,
                            label: datum.geography,
                            route: `geography=${datum.geography}`
                        };
                    }), 'Geography');
                    setChartConfig({

                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "phys_capacity") {
                    config = formatStockChartConfig(sortedData || [],
                        [{
                            name: 'Phys. Free',
                            field: 'physical_free',
                            dataLabels: {format: '{point.y:,.0f}'},
                            tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                        },
                            {
                                name: 'Phys. Used',
                                field: 'physical_used',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Allocated',
                                field: 'allocated',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Phys. Capacity',
                                field: 'physical_capacity',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Orphaned',
                                field: 'orphaned',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Snap',
                                field: 'snap',
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            }
                        ]);
                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.geography,
                            label: datum.geography,
                            route: `geography=${datum.geography}`
                        };
                    }), 'Geography');
                    setChartConfig({
                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "data_reduction") {
                    if (!searchParams.get("geography") && data[1]?.[0].geography) {
                        searchParams.set("geography", data[1][0].geography)
                        setSearchParams(searchParams, {replace: true});
                    }
                    config = formatStockChartConfig(sortedData || [],
                        [{name: 'Data Reduction Ratio', field: 'DRR',}]
                    );

                    setAllAdditionalParams((data?.[1] || []).map(datum => {
                        return {
                            value: datum.geography,
                            label: datum.geography,
                            route: `geography=${datum.geography}`
                        };
                    }), 'Geography');

                    setChartConfig({
                        colors: [...enterpriseTrendColors],
                        yAxis: {
                            title: {
                                text: 'Data Reduction Ratio'
                            },
                        }
                    });
                } else { // Performance
                    config = formatDualAxisStockChartConfig(sortedData || [],
                        [
                            {
                                name: 'IOPS',
                                field: 'iops',
                                yAxis: 0,
                                dataLabels: {format: '{point.y:,.0f}'},
                                tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                            },
                            {
                                name: 'Latency',
                                field: 'latency',
                                yAxis: 1
                            },
                        ]);
                    let newData = data?.[1]?.filter(datum => datum.geography);
                    setAllAdditionalParams((newData || []).map(datum => {
                        return {
                            value: datum.geography,
                            label: datum.geography,
                            route: `geography=${datum.geography}`
                        };
                    }), 'Geography');
                    setChartConfig({
                        colors: allPerformance,
                        yAxis: [
                            {
                                title: {
                                    text: 'IOPS',
                                },
                                opposite: false
                            },
                            {
                                title: {
                                    text: 'Latency (ms)',
                                },
                                opposite: true
                            }
                        ]
                    });
                }
                setFormattedChartData(config.series);
                break;
            case "all_datacenters":
                let findDcs = {};
                const foundDcsData = sortedData?.map(datum => {
                    if (!findDcs[datum.data_center]) {
                        findDcs[datum.data_center] = true;
                    }

                    const returnDatum = {}

                    for (let dcKey in datum) {
                        // check if in the key (no prototypes) and if it's capitalized
                        if (datum.hasOwnProperty(dcKey)) {
                            // lowercasing...
                            returnDatum[!dcKey.charAt(0).match(/[A-Z\u00C0-\u00DC]+/) ? dcKey : dcKey.toLowerCase()] = datum[dcKey]
                        }
                    }

                    return returnDatum;
                });

                if (chartType.value === "capacity") {
                    setAllAdditionalParams(allSpaceTypes, 'Space Type');
                    const fieldsDc = Object.keys(findDcs).map(dc => ({
                        name: dc,
                        field: (searchParams.get('space_type') || allSpaceTypes[0].value),
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));

                    config = formatStockChartConfig(foundDcsData || [], fieldsDc, 'data_center');
                    if ((searchParams.get('space_type') || allSpaceTypes[0].value).includes('percent')) {
                        setChartConfig({
                            ...chartConfig,
                            colors: enterpriseTrendColors,
                            yAxis: {
                                title: {
                                    text: '% Used'
                                },
                            }
                        });
                    } else {
                        setChartConfig({
                            ...chartConfig,
                            colors: enterpriseTrendColors,
                            yAxis: {
                                title: {
                                    text: 'GiB'
                                },
                            }
                        });
                    }
                } else if (chartType.value === "phys_capacity") {
                    setAllAdditionalParams(allPhysSpaceTypes, 'Space Type');
                    const fieldsDc = Object.keys(findDcs).map(dc => ({
                        name: dc,
                        field: (searchParams.get('space_type') || allPhysSpaceTypes[0].value),
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));

                    config = formatStockChartConfig(foundDcsData || [], fieldsDc, 'data_center');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "data_reduction") {
                    setAllAdditionalParams([], '');
                    const fieldsDc = Object.keys(findDcs).map(dc => ({
                        name: dc,
                        field: 'drr',
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));
                    config = formatStockChartConfig(foundDcsData || [], fieldsDc, 'data_center');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: 'Data Reduction Ratio'
                            },
                        }
                    });
                } else {
                    setAllAdditionalParams(allPerformanceTypes, 'Performance Type');
                    const fieldsDcPerf = Object.keys(findDcs).map(dc => ({
                        name: dc,
                        field: (searchParams.get('performance_type') || allPerformanceTypes[0].value),
                    }));
                    config = formatStockChartConfig(foundDcsData || [], fieldsDcPerf, 'data_center');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: searchParams.get('performance_type') || allPerformanceTypes[0].value
                            },
                        }
                    });
                }
                setFormattedChartData(config.series);
                break;
            case "all_company":
                let findComps = {};
                const foundCompsData = sortedData?.map(datum => {
                    if (!findComps[datum.company_name]) {
                        findComps[datum.company_name] = true;
                    }

                    const returnDatum = {}

                    for (let dcKey in datum) {
                        // check if in the key (no prototypes) and if it's capitalized
                        if (datum.hasOwnProperty(dcKey)) {
                            // lowercasing...
                            returnDatum[!dcKey.charAt(0).match(/[A-Z\u00C0-\u00DC]+/) ? dcKey : dcKey.toLowerCase()] = datum[dcKey]
                        }
                    }

                    return returnDatum;
                });

                if (chartType.value === "capacity") {
                    setAllAdditionalParams(allSpaceTypes, 'Space Type');
                    const fieldsComps = Object.keys(findComps).map(dc => ({
                        name: dc,
                        field: (searchParams.get('space_type') || allSpaceTypes[0].value),
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));

                    config = formatStockChartConfig(foundCompsData || [], fieldsComps, 'company_name');
                    if ((searchParams.get('space_type') || allSpaceTypes[0].value).includes('percent')) {
                        setChartConfig({
                            ...chartConfig,
                            colors: enterpriseTrendColors,
                            yAxis: {
                                title: {
                                    text: '% Used'
                                },
                            }
                        });
                    } else {
                        setChartConfig({
                            ...chartConfig,
                            colors: enterpriseTrendColors,
                            yAxis: {
                                title: {
                                    text: 'GiB'
                                },
                            }
                        });
                    }
                } else if (chartType.value === "phys_capacity") {
                    setAllAdditionalParams(allPhysSpaceTypes, 'Space Type');
                    const fieldsComps = Object.keys(findComps).map(dc => ({
                        name: dc,
                        field: (searchParams.get('space_type') || allPhysSpaceTypes[0].value),
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));

                    config = formatStockChartConfig(foundCompsData || [], fieldsComps, 'company_name');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "data_reduction") {
                    setAllAdditionalParams([], '');
                    const fieldsComps = Object.keys(findComps).map(dc => ({
                        name: dc,
                        field: 'drr',
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));
                    config = formatStockChartConfig(foundCompsData || [], fieldsComps, 'company_name');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: 'Data Reduction Ratio'
                            },
                        }
                    });
                } else {
                    setAllAdditionalParams(allPerformanceTypes, 'Performance Type');
                    const fieldsDcCompsPerf = Object.keys(findComps).map(dc => ({
                        name: dc,
                        field: (searchParams.get('performance_type') || allPerformanceTypes[0].value),
                    }));
                    config = formatStockChartConfig(foundCompsData || [], fieldsDcCompsPerf, 'company_name');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: searchParams.get('performance_type') || allPerformanceTypes[0].value
                            },
                        }
                    });
                }
                setFormattedChartData(config.series);
                break;
            case "all_geography":
                let findGeos = {};
                const foundGeosData = sortedData?.map(datum => {
                    if (!findGeos[datum.geography]) {
                        findGeos[datum.geography] = true;
                    }

                    const returnDatum = {}

                    for (let dcKey in datum) {
                        // check if in the key (no prototypes) and if it's capitalized
                        if (datum.hasOwnProperty(dcKey)) {
                            // lowercasing...
                            returnDatum[!dcKey.charAt(0).match(/[A-Z\u00C0-\u00DC]+/) ? dcKey : dcKey.toLowerCase()] = datum[dcKey]
                        }
                    }

                    return returnDatum;
                });

                if (chartType.value === "capacity") {
                    setAllAdditionalParams(allSpaceTypes, 'Space Type');
                    const fieldsGeo = Object.keys(findGeos).map(geo => ({
                        name: geo,
                        field: (searchParams.get('space_type') || allSpaceTypes[0].value),
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));

                    config = formatStockChartConfig(foundGeosData || [], fieldsGeo, 'geography');
                    if ((searchParams.get('space_type') || allSpaceTypes[0].value).includes('percent')) {
                        setChartConfig({
                            ...chartConfig,
                            colors: enterpriseTrendColors,
                            yAxis: {
                                title: {
                                    text: '% Used'
                                },
                            }
                        });
                    } else {
                        setChartConfig({
                            ...chartConfig,
                            colors: enterpriseTrendColors,
                            yAxis: {
                                title: {
                                    text: 'GiB'
                                },
                            }
                        });
                    }
                } else if (chartType.value === "phys_capacity") {
                    setAllAdditionalParams(allPhysSpaceTypes, 'Space Type');
                    const fieldsGeo = Object.keys(findGeos).map(geo => ({
                        name: geo,
                        field: (searchParams.get('space_type') || allPhysSpaceTypes[0].value),
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));

                    config = formatStockChartConfig(foundGeosData || [], fieldsGeo, 'geography');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "data_reduction") {
                    setAllAdditionalParams([], '');
                    const fieldsGeo = Object.keys(findGeos).map(geo => ({
                        name: geo,
                        field: 'drr',
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));
                    config = formatStockChartConfig(foundGeosData || [], fieldsGeo, 'company_name');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: 'Data Reduction Ratio'
                            },
                        }
                    });
                } else {
                    setAllAdditionalParams(allPerformanceTypes, 'Performance Type');
                    const fieldsGeoPerf = Object.keys(findGeos).map(geo => ({
                        name: geo,
                        field: (searchParams.get('performance_type') || allPerformanceTypes[0].value),
                    }));
                    config = formatStockChartConfig(foundGeosData || [], fieldsGeoPerf, 'geography');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: searchParams.get('performance_type') || allPerformanceTypes[0].value
                            },
                        }
                    });
                }
                setFormattedChartData(config.series);
                break;
            case "all_device_types":
                let findDts = {};
                const foundDtsData = data[0]?.map(datum => {
                    if (!findDts[datum.device_type_code]) {
                        findDts[datum.device_type_code] = true;
                    }

                    const returnDatum = {}

                    for (let dtKey in datum) {
                        // check if in the key (no prototypes) and if it's capitalized
                        if (datum.hasOwnProperty(dtKey)) {
                            // lowercasing...
                            returnDatum[!dtKey.charAt(0).match(/[A-Z\u00C0-\u00DC]+/) ? dtKey : dtKey.toLowerCase()] = datum[dtKey]
                        }
                    }

                    return returnDatum;
                });

                if (chartType.value === "capacity") {
                    setAllAdditionalParams(allSpaceTypes, 'Space Type');
                    const fieldsDt = Object.keys(findDts).map(dc => ({
                        name: dc,
                        field: (searchParams.get('space_type') || allSpaceTypes[0].value),
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));
                    config = formatStockChartConfig(foundDtsData || [], fieldsDt, 'device_type_code')
                    if ((searchParams.get('space_type') || allSpaceTypes[0].value).includes('percent')) {
                        setChartConfig({
                            ...chartConfig,
                            colors: enterpriseTrendColors,
                            yAxis: {
                                title: {
                                    text: '% Used'
                                },
                            }
                        });
                    } else {
                        setChartConfig({
                            ...chartConfig,
                            colors: enterpriseTrendColors,
                            yAxis: {
                                title: {
                                    text: 'GiB'
                                },
                            }
                        });
                    }
                } else if (chartType.value === "phys_capacity") {
                    setAllAdditionalParams(allPhysSpaceTypes, 'Space Type');
                    const fieldsDt = Object.keys(findDts).map(dc => ({
                        name: dc,
                        field: (searchParams.get('space_type') || allPhysSpaceTypes[0].value),
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));

                    config = formatStockChartConfig(foundDtsData || [], fieldsDt, 'device_type_code');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "data_reduction") {
                    setAllAdditionalParams([], '');
                    const fieldsDt = Object.keys(findDts).map(dc => ({
                        name: dc,
                        field: 'drr',
                    }));
                    config = formatStockChartConfig(foundDtsData?.sort((a, b) => new Date(a) - new Date(b)) || [], fieldsDt, 'device_type_code');

                    setChartConfig({
                        yAxis: {
                            title: {
                                text: 'Data Reduction Ratio'
                            },
                        }
                    });
                } else {
                    setAllAdditionalParams(allPerformanceTypes, 'Performance Type');
                    const fieldsDtsPerf = Object.keys(findDts).map(dc => ({
                        name: dc,
                        field: searchParams.get('performance_type') || allPerformanceTypes[0].value,
                    }));
                    config = formatStockChartConfig(foundDtsData || [], fieldsDtsPerf, 'device_type_code');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: searchParams.get('performance_type') || allPerformanceTypes[0].value
                            },
                        }
                    });
                }

                setFormattedChartData(config.series);
                break;
            case "all_classifications":
                let findCs = {};
                const foundCsData = data[0]?.map(datum => {
                    if (!findCs[datum.classification]) {
                        findCs[datum.classification] = true;
                    }

                    const returnDatum = {}

                    for (let dtKey in datum) {
                        // check if in the key (no prototypes) and if it's capitalized
                        if (datum.hasOwnProperty(dtKey)) {
                            // lowercasing...
                            returnDatum[!dtKey.charAt(0).match(/[A-Z\u00C0-\u00DC]+/) ? dtKey : dtKey.toLowerCase()] = datum[dtKey]
                        }
                    }

                    return returnDatum;
                });

                if (chartType.value === "capacity") {
                    setAllAdditionalParams(allSpaceTypes, 'Space Type');
                    const fieldsCs = Object.keys(findCs).map(dc => ({
                        name: dc,
                        field: (searchParams.get('space_type') || allSpaceTypes[0].value),
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));
                    config = formatStockChartConfig(foundCsData || [], fieldsCs, 'classification')
                    if ((searchParams.get('space_type') || allSpaceTypes[0].value).includes('percent')) {
                        setChartConfig({
                            ...chartConfig,
                            colors: enterpriseTrendColors,
                            yAxis: {
                                title: {
                                    text: '% Used'
                                },
                            }
                        });
                    } else {
                        setChartConfig({
                            ...chartConfig,
                            colors: enterpriseTrendColors,
                            yAxis: {
                                title: {
                                    text: 'GiB'
                                },
                            }
                        });
                    }
                } else if (chartType.value === "phys_capacity") {
                    setAllAdditionalParams(allPhysSpaceTypes, 'Space Type');
                    const fieldsCs = Object.keys(findCs).map(dc => ({
                        name: dc,
                        field: (searchParams.get('space_type') || allPhysSpaceTypes[0].value),
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));
                    config = formatStockChartConfig(foundCsData || [], fieldsCs, 'classification');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "data_reduction") {
                    setAllAdditionalParams([], '');
                    const fieldsCs = Object.keys(findCs).map(dc => ({
                        name: dc,
                        field: 'drr',
                    }));
                    config = formatStockChartConfig(foundCsData?.sort((a, b) => new Date(a) - new Date(b)) || [], fieldsCs, 'classification')

                    setChartConfig({
                        yAxis: {
                            title: {
                                text: 'Data Reduction Ratio'
                            },
                        }
                    });
                } else {
                    setAllAdditionalParams(allPerformanceTypes, 'Performance Type');
                    const fieldsCsPerf = Object.keys(findCs).map(dc => ({
                        name: dc,
                        field: (searchParams.get('performance_type') || allPerformanceTypes[0].value),
                    }));
                    config = formatStockChartConfig(foundCsData || [], fieldsCsPerf, 'classification');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: searchParams.get('performance_type') || allPerformanceTypes[0].value
                            },
                        }
                    });
                }
                setFormattedChartData(config.series);
                break;
            case "all_applications":
                let findAp = {};
                const foundApData = data[0]?.map(datum => {
                    if (!findAp[datum.application]) {
                        findAp[datum.application] = true;
                    }

                    const returnDatum = {}

                    for (let dtKey in datum) {
                        // check if in the key (no prototypes) and if it's capitalized
                        if (datum.hasOwnProperty(dtKey)) {
                            // lowercasing...
                            returnDatum[!dtKey.charAt(0).match(/[A-Z\u00C0-\u00DC]+/) ? dtKey : dtKey.toLowerCase()] = datum[dtKey]
                        }
                    }

                    return returnDatum;
                });

                if (chartType.value === "capacity") {
                    setAllAdditionalParams(allSpaceTypes, 'Space Type');
                    const fieldsAp = Object.keys(findAp).map(dc => ({
                        name: dc,
                        field: (searchParams.get('space_type') || allSpaceTypes[0].value),
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));
                    config = formatStockChartConfig(foundApData || [], fieldsAp, 'application')
                    if ((searchParams.get('space_type') || allSpaceTypes[0].value).includes('percent')) {
                        setChartConfig({
                            ...chartConfig,
                            colors: enterpriseTrendColors,
                            yAxis: {
                                title: {
                                    text: '% Used'
                                },
                            }
                        });
                    } else {
                        setChartConfig({
                            ...chartConfig,
                            colors: enterpriseTrendColors,
                            yAxis: {
                                title: {
                                    text: 'GiB'
                                },
                            }
                        });
                    }
                } else if (chartType.value === "phys_capacity") {
                    setAllAdditionalParams(allPhysSpaceTypes, 'Space Type');
                    const fieldsAp = Object.keys(findAp).map(dc => ({
                        name: dc,
                        field: (searchParams.get('space_type') || allPhysSpaceTypes[0].value),
                        dataLabels: {format: '{point.y:,.0f}'},
                        tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                    }));
                    config = formatStockChartConfig(foundApData || [], fieldsAp, 'application');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: 'GiB'
                            },
                        }
                    });
                } else if (chartType.value === "data_reduction") {
                    setAllAdditionalParams([], '');
                    const fieldsAp = Object.keys(findAp).map(dc => ({
                        name: dc,
                        field: 'drr',
                    }));
                    config = formatStockChartConfig(foundApData?.sort((a, b) => new Date(a) - new Date(b)) || [], fieldsAp, 'application')

                    setChartConfig({
                        yAxis: {
                            title: {
                                text: 'Data Reduction Ratio'
                            },
                        }
                    });
                } else {
                    setAllAdditionalParams(allPerformanceTypes, 'Performance Type');
                    const fieldsApPerf = Object.keys(findAp).map(dc => ({
                        name: dc,
                        field: (searchParams.get('performance_type') || allPerformanceTypes[0].value),
                    }));
                    config = formatStockChartConfig(foundApData || [], fieldsApPerf, 'application');
                    setChartConfig({
                        yAxis: {
                            title: {
                                text: searchParams.get('performance_type') || allPerformanceTypes[0].value
                            },
                        }
                    });
                }
                setFormattedChartData(config.series);
                break;
            default:
            // nothing
        }
    };

    const filterChanged = (eventData) => {
        let filteredData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            if (node.data) filteredData.push(node.data);
        });

        handleStateWithData([filteredData]);
    };

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>{`Enterprise ${chartType.label} ${chartMetric.label} Trend`}</VsiH4>
            <VsiBubble noAutobot>
                <div className={"w-100 pb-2 d-flex"}>
                    <VsiLabeledSelect options={chartMetric.value.includes('business') ? chartBUTypes : chartTypes}
                                      isLoading={isLoading}
                                      isDisabled={isLoading}
                                      label={'Type'}
                                      minWidth={225}
                                      onChange={onTypeChange}
                                      defaultValue={chartType}/>
                    <VsiLabeledSelect options={activeMetricSelection}
                                      isLoading={isLoading}
                                      isDisabled={isLoading}
                                      label={'Metric'}
                                      minWidth={200}
                                      maxWidth={500}
                                      onChange={onMetricChange}
                                      defaultValue={chartMetric}/>
                    {additionalChartOptionLabel && (
                        <VsiLabeledSelect options={additionalChartOptionSelection}
                                          isLoading={isLoading}
                                          isDisabled={isLoading}
                                          label={additionalChartOptionLabel}
                                          minWidth={200}
                                          maxWidth={600}
                                          onChange={onAdditionalOptionChange}
                                          defaultValue={additionalChartOption}/>
                    )}
                </div>
            </VsiBubble>

            <VsiBubble
                unit={chartType.value}
                autobotInfo={{
                    secondaryMetric: [chartMetric.value, additionalChartOption.value],
                    yAxes: chartConfig.yAxis
                }}
            >
                <Stockchart series={formattedChartData} config={chartConfig}/>
            </VsiBubble>
            <VsiBubble unit={chartType.value}
                       autobotInfo={{secondaryMetric: [chartMetric.value, additionalChartOption.value]}}>
                <Grid
                    isLoading={isLoading}
                    addStyle
                    data={(chartType.value === 'performance' && chartMetric.value === 'history') ? chartPerfData : chartData}
                    columnDefs={chartDefs}
                    saveParams={{
                        type: chartType.value,
                        metric: chartMetric.value,
                    }}
                    filterChanged={filterChanged}/>
            </VsiBubble>

        </Card>
    );
}
